import React from "react";
import { SafetyMark } from "./SafetyMark";
import { EmergencyFooter } from "./EmergencyFooter";
import { PANELS, useEmergencyContext } from "./EmergencyPanels";

/**
 * DDS-001 · Chapter 10 · §4 · The Five Choices — Landing.
 * ──────────────────────────────────────────────────────
 * Five choices. No more, no fewer. Sentence-case labels.
 * Generous spacing. The surface gets simpler under stress.
 *
 * Phase 1 scope (per Emergency Experience 001 · Phase 1):
 *   · Render the five choice cards.
 *   · Universal footer.
 *   · Each card is tappable; tapping opens a per-choice detail
 *     screen, rendering the live panels in EmergencyPanels.jsx inside
 *     each detail because the intelligence layer (playbooks,
 *     personal data from Traveller Memory C1, consular content)
 *     is Phase 2 / Phase 3 work.
 *
 * The five choices, their icons, and their subtitles are canonical
 * — do not add, remove, or reorder without a Chapter 10 amendment.
 */

const CHOICES = [
  {
    id: "medical",
    testid: "cc-emergency-choice-medical",
    label: "Medical",
    subtitle: "For illness, injury, allergies, or a shore incident.",
    // Simple stroke icon; DDS-001 Chapter 03 will replace these with
    // the ratified icon set on chapter ratification.
    Icon: MedicalIcon,
  },
  {
    id: "passport",
    testid: "cc-emergency-choice-passport",
    label: "Lost Passport",
    subtitle: "What to do given where you are right now.",
    Icon: PassportIcon,
  },
  {
    id: "missed-ship",
    testid: "cc-emergency-choice-missed-ship",
    label: "Missed the Ship",
    subtitle: "A short, ordered playbook. Calm and decisive.",
    Icon: ShipIcon,
  },
  {
    id: "contacts",
    testid: "cc-emergency-choice-contacts",
    label: "Emergency Contacts",
    subtitle: "Grouped for the moment — ship, cruise line, family, insurance.",
    Icon: PhoneShieldIcon,
  },
  {
    id: "safety-info",
    testid: "cc-emergency-choice-safety-info",
    label: "Today's Safety Information",
    subtitle: "Contextual to your voyage and today's port. Never generic.",
    Icon: CloudShieldIcon,
  },
];

function IconWrap({ children }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        width: 32,
        height: 32,
        alignItems: "center",
        justifyContent: "center",
        color: "var(--cc-safety-red)",
      }}
    >
      {children}
    </span>
  );
}

function MedicalIcon() {
  return (
    <IconWrap>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="3" width="6" height="18" rx="1" />
        <rect x="3" y="9" width="18" height="6" rx="1" />
      </svg>
    </IconWrap>
  );
}
function PassportIcon() {
  return (
    <IconWrap>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <circle cx="12" cy="11" r="3" />
        <path d="M8 17h8" />
      </svg>
    </IconWrap>
  );
}
function ShipIcon() {
  return (
    <IconWrap>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 17c1.5 1 3 1 4.5 0 1.5-1 3-1 4.5 0s3 1 4.5 0 3-1 4.5 0" />
        <path d="M4 13l1-5h14l1 5" />
        <path d="M12 4v4" />
      </svg>
    </IconWrap>
  );
}
function PhoneShieldIcon() {
  return (
    <IconWrap>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />
        <path d="M9.5 11.5l1.5 1.5c.5.5 1.5.5 2 0l1.5-1.5" />
      </svg>
    </IconWrap>
  );
}
function CloudShieldIcon() {
  return (
    <IconWrap>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 15a4 4 0 010-8 5 5 0 019.6-1A4.5 4.5 0 0117 15H7z" />
        <path d="M9 19h6" />
      </svg>
    </IconWrap>
  );
}

/**
 * A single choice card. Chapter 10 §2.6.
 */
function EmergencyChoiceCard({ choice, onOpen }) {
  const { id, label, subtitle, Icon, testid } = choice;
  const handleClick = React.useCallback(() => onOpen?.(id), [id, onOpen]);
  const handleKey = React.useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen?.(id);
      }
    },
    [id, onOpen],
  );
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testid}
      className="cc-emergency-card"
      onClick={handleClick}
      onKeyDown={handleKey}
      aria-label={label}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
        <Icon />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 600,
              lineHeight: 1.2,
              color: "#0f172a",
            }}
          >
            {label}
          </div>
          {subtitle ? (
            <div
              style={{
                fontSize: 14,
                color: "#64748b",
                marginTop: 6,
                lineHeight: 1.4,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * The full landing page.
 */
export function EmergencyLandingPage() {
  const [openChoice, setOpenChoice] = React.useState(null);

  const active = openChoice
    ? CHOICES.find((c) => c.id === openChoice) || null
    : null;

  return (
    <div
      data-testid="cc-emergency-landing"
      className="cc-emergency-landing"
      style={{ maxWidth: 720, marginLeft: "auto", marginRight: "auto" }}
    >
      {/* §2.5 · Header pattern — Safety mark + heading + reassurance. */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <SafetyMark size={40} variant="primary" />
        <div>
          <h1
            data-testid="cc-emergency-heading"
            style={{
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1.15,
              color: "#0f172a",
              margin: 0,
            }}
          >
            Safety &amp; Emergency
          </h1>
          <p
            style={{
              fontSize: 15,
              color: "#475569",
              margin: "6px 0 0 0",
              lineHeight: 1.4,
            }}
          >
            Your saved contacts and official guidance. What do you need?
          </p>
        </div>
      </header>

      {/* §4 · The five choices */}
      {!active ? (
        <div
          role="list"
          data-testid="cc-emergency-choice-list"
          style={{ display: "grid", gap: 24 }}
        >
          {CHOICES.map((c) => (
            <EmergencyChoiceCard
              key={c.id}
              choice={c}
              onOpen={setOpenChoice}
            />
          ))}
        </div>
      ) : (
        <ChoiceDetail
          choice={active}
          onBack={() => setOpenChoice(null)}
        />
      )}

      {/* §2.9 · Mandatory Universal Footer */}
      <div style={{ marginTop: 40 }}>
        <EmergencyFooter />
      </div>
    </div>
  );
}

/**
 * Detail screen for each choice — data from GET /api/emergency/context
 * (traveller-selected voyage), official sources with verification dates.
 * The intelligence layer (playbooks, personal data, consular content)
 * arrives in Phase 2 & Phase 3 per EMERGENCY-EXPERIENCE-001-DESIGN §Phasing.
 */
function ChoiceDetail({ choice, onBack }) {
  const { loading, data, error } = useEmergencyContext();
  // Location is chosen by the traveller — never inferred from the itinerary.
  const [location, setLocation] = React.useState("");
  const Panel = PANELS[choice.id];
  return (
    <div data-testid="cc-emergency-detail">
      <button
        type="button"
        data-testid="cc-emergency-back"
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: "#0f766e",
          fontWeight: 600,
          fontSize: 14,
          cursor: "pointer",
          padding: 0,
          marginBottom: 12,
        }}
      >
        &larr; Back to choices
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <choice.Icon />
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{choice.label}</div>
      </div>
      {loading && <p style={{ fontSize: 14, color: "#64748b", marginTop: 12 }} data-testid="cc-emergency-loading">Loading your saved details…</p>}
      {!loading && error === "no-voyage" && (
        <p style={{ fontSize: 14, color: "#b45309", marginTop: 12 }} data-testid="cc-emergency-no-voyage">
          No voyage is selected. Choose a cruise first — contacts and guidance are voyage-specific.
        </p>
      )}
      {!loading && error === "error" && (
        <p style={{ fontSize: 14, color: "#b45309", marginTop: 12 }} data-testid="cc-emergency-error">
          Your saved details could not be loaded right now. Official resources below still apply.
        </p>
      )}
      {!loading && Panel && <Panel ctx={data} location={location} setLocation={setLocation} />}
    </div>
  );
}

export default EmergencyLandingPage;
